import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { useTranslation } from 'react-i18next'
import { Stack, Typography } from '@vapor/material'
import { useState } from 'react'
import CityTaxExonerationLookup from 'src/app/components/city-tax-exoneration-lookup/city-tax-exoneration-lookup'
import { useProperty, useTenant } from 'src/app/hooks/context/context.hook'
import { FormItem, HICLoading } from 'hic-components'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { CityTaxExonerationFragment, useUpdateProductionMutation } from 'src/api/api.generated'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { GraphQLError } from 'graphql'
import { ProductionAssignedReturn, UnionBill } from './production-assigned-actions.type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'

type ProductionAssignedActionEditCityTaxProps = {
  title: string
  open: boolean
  onClose: () => void
  bills: UnionBill[]
  productionAssignedIds: string[]
  getProductionAssigned: (bills: UnionBill[], productionAssignedId: string) => ProductionAssignedReturn
}
export function ProductionAssignedActionEditCityTax({ title, open, onClose, productionAssignedIds, getProductionAssigned, bills }: ProductionAssignedActionEditCityTaxProps) {
  const { t } = useTranslation()
  const propertyId = useProperty()?.id ?? ''
  const { currency, id: tenantId } = useTenant()
  const localizeString = useLocalizeString()
  const [amount, setAmount] = useState<number | null>(null)
  const [exoneration, setExoneration] = useState<CityTaxExonerationFragment | null>(null)
  const [loading, setLoading] = useState(false)

  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const [updateProduction] = useUpdateProductionMutation()

  async function submit() {
    const errors: (readonly GraphQLError[])[] = []
    setLoading(true)
    await Promise.all(
      productionAssignedIds.map(async (id) => {
        if (id) {
          const production = getProductionAssigned(bills, id)?.production
          if (!production) return

          const originalAmount = production?.amount ?? 0
          const chargeCategoryId = 'chargeCategoryId' in production ? production.chargeCategoryId : production?.chargeCategory.id ?? ''
          const vatId = 'vatId' in production ? production.vatId : production?.vat.id ?? ''
          const amountToUse = amount ?? originalAmount
          const amountToSend = amountToUse - amountToUse * (exoneration?.discount ?? 0)
          const description = exoneration?.description ? localizeString(exoneration.description) : production?.description ?? ''

          const result = await updateProduction({
            variables: {
              id: production.id,
              value: {
                amount: amountToSend,
                cityTax: {
                  guestId: production.cityTax?.guestId ?? undefined,
                  exoneration: exoneration?.code ?? undefined,
                },
                chargeCategoryId: chargeCategoryId,
                currency: production?.currency ?? '',
                date: production?.date,
                quantity: production?.quantity,
                total: amountToSend * production?.quantity,
                vatId: vatId,
                tenantId,
                propertyId,
                description,
              },
            },
            refetchQueries: ['ReservationDetail'],
            onError: () => {
              setLoading(false)
            },
          })

          if (result.errors) errors.push(result.errors)
        }
      }),
    )
    setLoading(false)
    if (errors.length > 0) {
      showError(errors.join(';'))
    } else {
      showSuccess(t('pms.booking.passerby-bill.creation-dialog.update_success'))
      onClose()
    }
  }

  return (
    <FormLayout //
      title={title}
      appearance={{ type: 'DIALOG', open: open, onClose: onClose }}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: submit,
            text: t('commons.confirm'),
          }}
          cancelButton={{ clickHandler: onClose }}
        />
      }
    >
      <Stack minWidth={{ xs: '100%', sm: 400 }} minHeight={200} gap={2}>
        {loading && <HICLoading />}
        <Typography variant="body">{t('pms.booking.reservation.productions_assigned.edit_city_tax.info')}</Typography>
        <FormItem fieldLabel={t('pms.booking.reservation.productions_assigned.edit_city_tax.amount')}>
          <HicAmountField
            size="small"
            fullWidth
            value={amount}
            nonNegative
            sx={{ maxWidth: '100px' }}
            currency={currency}
            onChange={(value) => {
              setAmount(value ?? null)
            }}
          />
        </FormItem>
        <FormItem fieldLabel={t('pms.booking.reservation.productions_assigned.edit_city_tax.exoneration')} sx={{ maxWidth: '200px' }}>
          <CityTaxExonerationLookup
            size="small"
            propertyId={propertyId}
            value={exoneration}
            onChange={(exoneration) => {
              setExoneration(exoneration ?? null)
            }}
          />
        </FormItem>
      </Stack>
    </FormLayout>
  )
}
